import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import AppUpdateBanner from "./components/AppUpdateBanner";

// Pages
import LandingPage from "./pages/LandingPage";
import OnboardingPage from "./pages/OnboardingPage";
import LoginPage from "./pages/LoginPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import ClientLoginPage from "./pages/ClientLoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DocsPage from "./pages/DocsPage";
import ChangelogPage from "./pages/ChangelogPage";
import TicketsPage from "./pages/TicketsPage";
import { MentionsLegales, PolitiqueConfidentialite, CGU, ContactPage, SupportPage } from "./pages/LegalPages";
import AdminDashboard from "./pages/admin/AdminDashboard";
import CompanyDashboard from "./pages/company/CompanyDashboard";
import ClientDashboard from "./pages/client/ClientDashboard";
import CollaboratorDashboard from "./pages/collaborator/CollaboratorDashboard";
import BookingWidget from "./pages/widget/BookingWidget";
import PunctualWidget from "./pages/widget/PunctualWidget";
import SubscriptionWidget from "./pages/widget/SubscriptionWidget";
import RecurringWidget from "./pages/widget/RecurringWidget";
import AppointmentWidget from "./pages/widget/AppointmentWidget";
import DispatcherWidget from "./pages/widget/DispatcherWidget";
import PaymentSuccessPage from "./pages/widget/PaymentSuccessPage";

// Context
import { AuthProvider, useAuth } from "./context/AuthContext";
import ImpersonationBanner from "./components/ImpersonationBanner";
import { ThemeProvider } from "./context/ThemeContext";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <>
      <ImpersonationBanner />
      <Routes>
      {/* Public Pages */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/admin-login" element={<AdminLoginPage />} />
      <Route path="/connexion/:companySlug" element={<ClientLoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/docs" element={<DocsPage />} />
      <Route path="/changelog" element={<ChangelogPage />} />
      <Route path="/tickets" element={<TicketsPage />} />
      
      {/* Legal Pages */}
      <Route path="/legal/mentions" element={<MentionsLegales />} />
      <Route path="/legal/privacy" element={<PolitiqueConfidentialite />} />
      <Route path="/legal/cgu" element={<CGU />} />
      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/confidentialite" element={<PolitiqueConfidentialite />} />
      <Route path="/cgu" element={<CGU />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/support" element={<SupportPage />} />
      
      {/* Admin Routes */}
      <Route path="/admin/*" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      
      {/* Company Routes */}
      <Route path="/company/*" element={
        <ProtectedRoute allowedRoles={["company"]}>
          <CompanyDashboard />
        </ProtectedRoute>
      } />
      
      {/* Client Routes */}
      <Route path="/client/*" element={
        <ProtectedRoute allowedRoles={["client"]}>
          <ClientDashboard />
        </ProtectedRoute>
      } />
      
      {/* Collaborator Routes */}
      <Route path="/collaborator/*" element={
        <ProtectedRoute allowedRoles={["collaborator"]}>
          <CollaboratorDashboard />
        </ProtectedRoute>
      } />
      
      {/* Public Widgets */}
      <Route path="/widget/:companyId" element={<BookingWidget />} />
      <Route path="/widget/dispatcher/:widgetId" element={<DispatcherWidget />} />
      <Route path="/widget/subscription/:widgetId" element={<SubscriptionWidget />} />
      <Route path="/widget/recurring/:widgetId" element={<RecurringWidget />} />
      <Route path="/widget/punctual/:widgetId" element={<PunctualWidget />} />
      <Route path="/widget/appointment/:widgetId" element={<AppointmentWidget />} />
      <Route path="/widget/payment/success" element={<PaymentSuccessPage />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <div className="min-h-screen bg-background noise-overlay">
            <AppRoutes />
            <Toaster position="top-right" richColors />
            <AppUpdateBanner />
          </div>
        </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
